// src/features/provider_configs/new.tsx

import { useEffect } from "react";
import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  CheckboxField,
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import { generateDefaultValues, parseYes } from "@/features/FormCard/utils";

// Import select options from filter.tsx - do NOT modify filter.tsx
import {
  docTypeOptions,
  extractTypeOptions,
  frequencyOptions,
  loadTypeOptions,
  statusOptions,
  transformTypeOptions,
} from "./filter";
import {
  UNZIP_STRATEGY_VALUES,
  unzipStrategyOptions,
} from "./unzipStrategy";

// file_type uses the same options as extract_type
const fileTypeOptions = extractTypeOptions;

const client = "etl-quantilia";
const table_name = "provider_configs";

// Zod schema for the new form
const FormSchema = z.object({
  file_pattern: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  source_folder: z.string().nullable().optional(),
  connector_config: z
    .union([
      z.string().nullable().optional(),
      z.object({
        id: z.number(),
        text: z.string().nullable().optional(),
      }),
    ])
    .nullable()
    .optional(),
  connector_config_id: z.coerce.number().nullable().optional(),
  unzip_strategy: z.enum(UNZIP_STRATEGY_VALUES).nullable().optional(),
  file_type: z.coerce.number().optional().nullable(),
  extract_type: z.coerce.number().optional().nullable(),
  transform_type: z.coerce.number().optional().nullable(),
  load_type: z.coerce.number().optional().nullable(),
  status: z.coerce.number().optional(),
  title: z.string().nullable().optional(),
  priority: z.coerce.number().optional(),
  frequency: z.coerce.number().optional(),
  doc_type: z.string().nullable().optional(),
  keep_sheet: z.string().nullable().optional(),
  doc_version: z.coerce.number().optional(),
  is_client_config: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  is_python_etl: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  py_type: z.string().nullable().optional(),
  py_extract: z.string().nullable().optional(),
  py_transform: z.string().nullable().optional(),
  py_load: z.string().nullable().optional(),
  py_preprocess: z.string().nullable().optional(),
  py_preprocess_parameter_store_gpg_passphrase:
    z.string().nullable().optional(),
  py_preprocess_parameter_store_gpg_private:
    z.string().nullable().optional(),
  py_preprocess_zip_password: z.string().nullable().optional(),
  py_extract_encoding: z.string().nullable().optional(),
  py_extract_header: z.coerce.number().nullable().optional(),
  py_extract_engine: z.string().nullable().optional(),
  py_extract_skipfooter: z.coerce.number().nullable().optional(),
  py_extract_concat_all_sheets: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  py_transform_reporting_date: z.string().nullable().optional(),
  py_transform_strict_mapping: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),
  py_col_sep: z.string().nullable().optional(),
  py_sheet_name: z.string().nullable().optional(),
  py_skiprows: z.coerce.number().nullable().optional(),
  py_thousand_sep: z.string().nullable().optional(),
  py_control_file_version: z.string().nullable().optional(),
  py_table: z.string().nullable().optional(),
  py_extract_config: z.string().nullable().optional(),
  py_transform_config: z.string().nullable().optional(),

  // NEW: multi select companies (ids)
  company_ids: z.array(z.coerce.number()).nullable().optional(),

  transfer_col_position: z.coerce.number().optional(),
  client: z.string().nullable().optional(),
  mime_type: z.string().nullable().optional(),
  template_s3_key: z.string().nullable().optional(),
  usage_description: z.string().nullable().optional(),
  settings: z.string().nullable().optional(),
});

export function ProviderConfigsNew() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
    adjustData: (data: any) => ({
      ...data,
      company_ids:
        Array.isArray(data.company_ids) && data.company_ids.length
          ? data.company_ids.map((x: any) => Number(x))
          : null,
    }),
  });

  const navigateToList = () => {
    navigate(-1);
  };

  const DEFAULTS = {
    ...generateDefaultValues(FormSchema, {}),
    company_ids: null,
  };
  const isClientConfig = Boolean(form.watch("is_client_config"));
  const isPythonEtl = Boolean(form.watch("is_python_etl"));

  useEffect(() => {
    if (!isClientConfig) {
      form.setValue("client", null);
      form.setValue("mime_type", null);
      form.setValue("template_s3_key", null);
      form.setValue("usage_description", null);
    }
  }, [isClientConfig, form]);

  const onResetNew = () => {
    form.reset(DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
      keepValues: false,
    });
    handleReset();
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("actions.new")}{" "}
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Provider Configurations",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="code"
                    label={t(`${table_name}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="file_pattern"
                    label={t(`${table_name}.columns.file_pattern`, {
                      ns: "tables",
                      defaultValue: "File Pattern",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="source_folder"
                    label={t(`${table_name}.columns.source_folder`, {
                      ns: "tables",
                      defaultValue: "Source Folder",
                    })}
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="connector_config"
                    label="Connector Config"
                    base_url="etl-quantilia/connector_configs"
                    column_name="path"
                    id_column="connector_config_id"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="unzip_strategy"
                    label={t(`${table_name}.columns.unzip_strategy`, {
                      ns: "tables",
                      defaultValue: "Unzip Strategy",
                    })}
                    options={unzipStrategyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="file_type"
                    label={t(`${table_name}.columns.file_type`, {
                      ns: "tables",
                      defaultValue: "File Type",
                    })}
                    options={fileTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="extract_type"
                    label={t(`${table_name}.columns.extract_type`, {
                      ns: "tables",
                      defaultValue: "Extract Type",
                    })}
                    options={extractTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="transform_type"
                    label={t(`${table_name}.columns.transform_type`, {
                      ns: "tables",
                      defaultValue: "Transform Type",
                    })}
                    options={transformTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="load_type"
                    label={t(`${table_name}.columns.load_type`, {
                      ns: "tables",
                      defaultValue: "Load Type",
                    })}
                    options={loadTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="status"
                    label={t(`${table_name}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    options={statusOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="title"
                    label={t(`${table_name}.columns.title`, {
                      ns: "tables",
                      defaultValue: "Title",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="priority"
                    label={t(`${table_name}.columns.priority`, {
                      ns: "tables",
                      defaultValue: "Priority",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <SelectField
                    name="frequency"
                    label={t(`${table_name}.columns.frequency`, {
                      ns: "tables",
                      defaultValue: "Frequency",
                    })}
                    options={frequencyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="doc_type"
                    label={t(`${table_name}.columns.doc_type`, {
                      ns: "tables",
                      defaultValue: "Document Type",
                    })}
                    options={docTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="keep_sheet"
                    label={t(`${table_name}.columns.keep_sheet`, {
                      ns: "tables",
                      defaultValue: "Keep Sheet",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="doc_version"
                    label={t(`${table_name}.columns.document_version`, {
                      ns: "tables",
                      defaultValue: "Document Version",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="company_ids"
                    label={t(`${table_name}.columns.companies`, {
                      ns: "tables",
                      defaultValue: "Companies",
                    })}
                    base_url="instit-quantilia/companies"
                    control={form.control}
                    isMulti
                  />
                  <div />
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <TextareaField
                    name="settings"
                    label={t(`${table_name}.columns.settings`, {
                      ns: "tables",
                      defaultValue: "Settings (JSON)",
                    })}
                    control={form.control}
                    placeholder={t(`${table_name}.placeholders.settings`, {
                      ns: "tables",
                      defaultValue: "Enter JSON settings",
                    })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CheckboxField
                    name="is_client_config"
                    label={t(`${table_name}.columns.client_config`, {
                      ns: "tables",
                      defaultValue: "Client Config",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_python_etl"
                    label={t(`${table_name}.columns.python_etl`, {
                      ns: "tables",
                      defaultValue: "Python ETL",
                    })}
                    control={form.control}
                  />
                </div>

                {isClientConfig && (
                  <>
                    <Divider />
                    <div className="text-sm font-semibold text-gray-700">
                      {t(`${table_name}.sections.client_config`, {
                        ns: "tables",
                        defaultValue: "Client configuration",
                      })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InputField
                        name="client"
                        label={t(`${table_name}.columns.client`, {
                          ns: "tables",
                          defaultValue: "Client",
                        })}
                        control={form.control}
                      />
                      <InputField
                        name="mime_type"
                        label={t(`${table_name}.columns.mime_type`, {
                          ns: "tables",
                          defaultValue: "MIME Type",
                        })}
                        control={form.control}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <InputField
                        name="template_s3_key"
                        label={t(`${table_name}.columns.template_s3_key`, {
                          ns: "tables",
                          defaultValue: "Template S3 Key",
                        })}
                        control={form.control}
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <TextareaField
                        name="usage_description"
                        label={t(`${table_name}.columns.usage_description`, {
                          ns: "tables",
                          defaultValue: "Usage Description",
                        })}
                        control={form.control}
                      />
                    </div>
                  </>
                )}

                {isPythonEtl && (
                  <>
                    <Divider />
                    <div className="text-sm font-semibold text-gray-700">
                      {t(`${table_name}.sections.python_etl`, {
                        ns: "tables",
                        defaultValue: "Python ETL configuration",
                      })}
                    </div>

                    <div className="space-y-6">
                      <section className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          General
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            name="py_type"
                            label={t(`${table_name}.columns.py_type`, {
                              ns: "tables",
                              defaultValue: "Type",
                            })}
                            control={form.control}
                          />
                          <InputField
                            name="py_table"
                            label={t(`${table_name}.columns.py_table`, {
                              ns: "tables",
                              defaultValue: "Table",
                            })}
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <InputField
                            name="py_preprocess"
                            label={t(`${table_name}.columns.py_preprocess`, {
                              ns: "tables",
                              defaultValue: "Preprocess",
                            })}
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <InputField
                            name="py_extract"
                            label={t(`${table_name}.columns.py_extract`, {
                              ns: "tables",
                              defaultValue: "Extract",
                            })}
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <InputField
                            name="py_transform"
                            label={t(`${table_name}.columns.py_transform`, {
                              ns: "tables",
                              defaultValue: "Transform",
                            })}
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <InputField
                            name="py_load"
                            label={t(`${table_name}.columns.py_load`, {
                              ns: "tables",
                              defaultValue: "Load",
                            })}
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            name="py_control_file_version"
                            label={t(
                              `${table_name}.columns.py_control_file_version`,
                              {
                                ns: "tables",
                                defaultValue: "Control File Version",
                              },
                            )}
                            control={form.control}
                          />
                          <div />
                        </div>
                      </section>

                      <section className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Preprocess
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <InputField
                            name="py_preprocess_parameter_store_gpg_passphrase"
                            label={t(
                              `${table_name}.columns.py_preprocess_parameter_store_gpg_passphrase`,
                              {
                                ns: "tables",
                                defaultValue: "GPG Passphrase Parameter Store",
                              },
                            )}
                            control={form.control}
                          />
                          <InputField
                            name="py_preprocess_parameter_store_gpg_private"
                            label={t(
                              `${table_name}.columns.py_preprocess_parameter_store_gpg_private`,
                              {
                                ns: "tables",
                                defaultValue: "GPG Private Key Parameter Store",
                              },
                            )}
                            control={form.control}
                          />
                          <InputField
                            name="py_preprocess_zip_password"
                            label={t(
                              `${table_name}.columns.py_preprocess_zip_password`,
                              {
                                ns: "tables",
                                defaultValue: "ZIP Password Parameter Store",
                              },
                            )}
                            control={form.control}
                          />
                        </div>
                      </section>

                      <section className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Extract
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            name="py_col_sep"
                            label={t(`${table_name}.columns.py_col_sep`, {
                              ns: "tables",
                              defaultValue: "Column Separator",
                            })}
                            control={form.control}
                          />
                          <InputField
                            name="py_thousand_sep"
                            label={t(`${table_name}.columns.py_thousand_sep`, {
                              ns: "tables",
                              defaultValue: "Thousand Separator",
                            })}
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            name="py_sheet_name"
                            label={t(`${table_name}.columns.py_sheet_name`, {
                              ns: "tables",
                              defaultValue: "Sheet Name",
                            })}
                            control={form.control}
                          />
                          <InputField
                            name="py_skiprows"
                            label={t(`${table_name}.columns.py_skiprows`, {
                              ns: "tables",
                              defaultValue: "Skip Rows",
                            })}
                            type="number"
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            name="py_extract_encoding"
                            label={t(`${table_name}.columns.py_extract_encoding`, {
                              ns: "tables",
                              defaultValue: "Encoding",
                            })}
                            control={form.control}
                          />
                          <InputField
                            name="py_extract_header"
                            label={t(`${table_name}.columns.py_extract_header`, {
                              ns: "tables",
                              defaultValue: "Header",
                            })}
                            type="number"
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            name="py_extract_engine"
                            label={t(`${table_name}.columns.py_extract_engine`, {
                              ns: "tables",
                              defaultValue: "Engine",
                            })}
                            control={form.control}
                          />
                          <InputField
                            name="py_extract_skipfooter"
                            label={t(
                              `${table_name}.columns.py_extract_skipfooter`,
                              {
                                ns: "tables",
                                defaultValue: "Skip Footer",
                              },
                            )}
                            type="number"
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <CheckboxField
                            name="py_extract_concat_all_sheets"
                            label={t(
                              `${table_name}.columns.py_extract_concat_all_sheets`,
                              {
                                ns: "tables",
                                defaultValue: "Concat all sheets",
                              },
                            )}
                            control={form.control}
                          />
                          <div />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <TextareaField
                            name="py_extract_config"
                            label={t(`${table_name}.columns.py_extract_config`, {
                              ns: "tables",
                              defaultValue: "Extract config (JSON)",
                            })}
                            control={form.control}
                            placeholder={t(
                              `${table_name}.placeholders.py_extract_config`,
                              {
                                ns: "tables",
                                defaultValue: "Enter extract JSON config",
                              },
                            )}
                          />
                        </div>
                      </section>

                      <section className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Transform
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <InputField
                            name="py_transform_reporting_date"
                            label={t(
                              `${table_name}.columns.py_transform_reporting_date`,
                              {
                                ns: "tables",
                                defaultValue: "Reporting Date",
                              },
                            )}
                            control={form.control}
                          />
                          <CheckboxField
                            name="py_transform_strict_mapping"
                            label={t(
                              `${table_name}.columns.py_transform_strict_mapping`,
                              {
                                ns: "tables",
                                defaultValue: "Strict Mapping",
                              },
                            )}
                            control={form.control}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                          <TextareaField
                            name="py_transform_config"
                            label={t(`${table_name}.columns.py_transform_config`, {
                              ns: "tables",
                              defaultValue: "Transform config (JSON)",
                            })}
                            control={form.control}
                            placeholder={t(
                              `${table_name}.placeholders.py_transform_config`,
                              {
                                ns: "tables",
                                defaultValue: "Enter transform JSON config",
                              },
                            )}
                          />
                        </div>
                      </section>
                    </div>
                  </>
                )}

              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={onResetNew}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields ? NewDiff(changedFields, []) : t("no_changes")}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
